import React, { useState } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { apiErrorMessage } from "../extenderPopCarrierAccessData";
import { assignExtenderPopCpeRouterAPI, type CpeRouterAssignment } from "../requests/request";
import { assignmentResultText, cleanHostname } from "./cpeRouterAssignment";

interface Device {
  id: number;
  serviceId: string;
  name: string | null;
}

type RowState = { hostname: string; busy: boolean; message: string | null; isError: boolean };

const EMPTY_ROW: RowState = { hostname: "", busy: false, message: null, isError: false };

/**
 * Admin Center → CPE Backhaul: tie each CPE device to its router by hostname
 * (CTX-829). A CPE is born from its Odoo line without a router; networking
 * installs the device and registers it in the inventory, and this is where
 * a Global Admin says which router it is. The API keeps the rules (the
 * router exists, is no other CPE's, and sits with the site's other devices)
 * and answers each refusal with its reason, shown on the row.
 */
const CpeRouterAssignmentCard: React.FC<{
  extenderPopId: number;
  tenantId: number;
  devices: Device[];
  /** Called after a router changes: the site's legs are derived again. */
  onChanged: () => void;
}> = ({ extenderPopId, tenantId, devices, onChanged }) => {
  const api = useAxiosAuth0();
  const [rows, setRows] = useState<Record<number, RowState>>({});
  const rowOf = (id: number) => rows[id] ?? EMPTY_ROW;
  const patch = (id: number, next: Partial<RowState>) =>
    setRows((current) => ({ ...current, [id]: { ...(current[id] ?? EMPTY_ROW), ...next } }));

  const assign = async (device: Device) => {
    const hostname = cleanHostname(rowOf(device.id).hostname);
    if (!hostname) return;
    patch(device.id, { busy: true, message: null, isError: false });
    try {
      const { data } = await assignExtenderPopCpeRouterAPI(api, extenderPopId, device.id, tenantId, hostname);
      const result = data?.data as CpeRouterAssignment;
      patch(device.id, { busy: false, message: assignmentResultText(result), isError: false });
      if (result.changed) onChanged();
    } catch (error) {
      patch(device.id, {
        busy: false,
        message: apiErrorMessage(error, "Could not assign the router"),
        isError: true,
      });
    }
  };

  return (
    <GenericCard
      headerContent="CPE Devices · Router"
      isCollapsible={false}
      bodyContent={
        <div className="flex flex-col gap-4">
          <p className="text-sm text-semantic-text-secondary">
            Type the hostname of the router of each device, as the inventory has it (for example KE.TUCHP.RT1).
          </p>
          {devices.map((device) => {
            const row = rowOf(device.id);
            return (
              <div key={device.id} className="flex flex-col gap-1">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="min-w-[12rem] text-sm">
                    <span className="block font-semibold text-semantic-text-primary">
                      {device.name || device.serviceId}
                    </span>
                    <span className="block text-semantic-text-secondary">{device.serviceId}</span>
                  </div>
                  <GenericInput
                    label="Router hostname"
                    placeholder="KE.SITE.RT01"
                    value={row.hostname}
                    disabled={row.busy}
                    onChange={(hostname) => patch(device.id, { hostname, message: null })}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") assign(device);
                    }}
                  />
                  <GenericButton
                    size="sm"
                    label={row.busy ? "Assigning…" : "Assign router"}
                    disabled={row.busy || !cleanHostname(row.hostname)}
                    onClick={() => assign(device)}
                  />
                </div>
                {row.message && (
                  <p
                    role={row.isError ? "alert" : "status"}
                    className={`text-sm ${row.isError ? "text-semantic-text-error" : "text-semantic-text-secondary"}`}
                  >
                    {row.message}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      }
    />
  );
};

export default CpeRouterAssignmentCard;
